export type IdeaFaq = { question: string; answer: string };

export type IdeaMakeWith = {
  featurePath: string;
  featureLabel: string;
  createPath: string;
};

export type IdeaEntry = { title: string; note: string };

export type IdeaSection = {
  id: string;
  title: string;
  description?: string;
  ideas: IdeaEntry[];
};

export type IdeaNiche = {
  slug: string;
  navTitle: string;
  keyword: string;
  seoDescription: string;
  intro: string;
  howToPick?: string[];
  makeWith: IdeaMakeWith;
  sections: IdeaSection[];
  faqs: IdeaFaq[];
  related: string[];
  legacy?: true;
};

export const IDEA_NICHES: IdeaNiche[] = [
  {
    slug: "interview-clip-hook-ideas",
    navTitle: "Interview Clip Hook Ideas",
    keyword: "Interview Clip Hook Ideas for Podcast and Interview Guests",
    seoDescription:
      "Interview clip hook ideas for podcast and interview guests, grouped by hook type, each with what to listen for and where to open the cut.",
    intro:
      "These are hook types to listen for when a guest sits down for a podcast or an interview, each with what to listen for and where to open the cut. They are grouped by the kind of opening they produce, from confessions to contrarian takes to numbers, so you can scan a transcript for one type at a time and mark every timestamp that matches.",
    howToPick: [
      "Start the cut on the line that creates tension, never on the host introducing the topic.",
      "Prefer hooks that work with the sound off, because the opening caption is what stops the scroll.",
      "Pick moments where something concrete is said: a number, a name, a decision or a refusal.",
      "Cut one hook per clip, and post the same moment twice with different openings if two hooks compete.",
    ],
    makeWith: {
      featurePath: "/features/find-viral-moments",
      featureLabel: "Find Viral Moments",
      createPath: "/create/find-moments",
    },
    sections: [
      {
        id: "confession-hooks",
        title: "Confession hooks",
        description:
          "Moments where the guest admits something, which is the oldest reason to keep watching.",
        ideas: [
          {
            title: "I Have Never Said This Publicly",
            note: "Guests flag their own confessions with a phrase like this, so start the cut on it. Everything before it is setup the viewer does not need.",
          },
          {
            title: "The Mistake That Cost Them The Most",
            note: "Ask for the most expensive mistake and clip the moment they name it. Put the cost on screen as they say it.",
          },
          {
            title: "What They Would Tell Their Younger Self",
            note: "A familiar question that still produces a specific regret from most guests. Cut the generic opener and start on the specific thing.",
          },
          {
            title: "The Belief They Held For Years That Was Wrong",
            note: "Clip the sentence where they admit the belief and the moment they say what changed it. Two beats, one clip.",
          },
          {
            title: "The Part Of The Job They Secretly Hate",
            note: "Delivery drops when people say this, often with a laugh. Keep the laugh, because it reads as honesty.",
          },
          {
            title: "The Deal They Should Have Walked Away From",
            note: "Business guests rarely volunteer this and usually pause before answering. Keep the pause in the cut.",
          },
          {
            title: "The Time They Nearly Quit",
            note: "Look for the moment the story turns from struggle to decision. Start just before the turn so the viewer feels it land.",
          },
          {
            title: "What They Pretend To Understand",
            note: "An expert admitting a blind spot in their own field is unexpected and quotable. Caption it with the exact phrase.",
          },
        ],
      },
      {
        id: "contrarian-takes",
        title: "Contrarian takes and rebuttals",
        description:
          "Claims that cut against what the audience assumes, and the pushback that follows.",
        ideas: [
          {
            title: "Everyone In Their Industry Is Wrong About This",
            note: "Caption the consensus in one line, then let the guest contradict it. Keep the setup under two seconds.",
          },
          {
            title: "The Popular Advice They Tell People To Ignore",
            note: "Clip the moment they name the advice and the one sentence reason. Put the advice itself in the opening text so the disagreement starts before playback.",
          },
          {
            title: "The Host Pushes Back And The Guest Holds",
            note: "Start on the host challenge, not on the original claim. The exchange is the clip.",
          },
          {
            title: "The Hot Take They Walk Back Mid Sentence",
            note: "A claim followed by a visible softening is more human than a clean one. Clip both halves.",
          },
          {
            title: "Why The Hype Is Overrated",
            note: "A guest dismissing a trend the audience is excited about gives the comments a fight. Keep it unedited so nothing looks taken out of context.",
          },
          {
            title: "The Rebuttal To A Viral Clip",
            note: "When a guest responds to something that circulated, clip only the response and caption what it responds to. Do not include the original footage.",
          },
          {
            title: "The Thing They Refuse To Do That Everyone Else Does",
            note: "Frame it as a rule they live by. The refusal is the hook and the reason is the payoff.",
          },
          {
            title: "Two Guests Who Disagree On Camera",
            note: "For panel episodes, find the exchange where one guest corrects another. Keep both faces in frame if you recorded video.",
          },
        ],
      },
      {
        id: "number-hooks",
        title: "Number and specifics hooks",
        description:
          "Concrete figures and precise details that anchor a clip in the first second.",
        ideas: [
          {
            title: "The Exact Figure They Lost",
            note: "Any number attached to a failure is a hook. Put the figure on screen the moment it is said and start the cut one sentence before.",
          },
          {
            title: "How Many Times They Were Rejected",
            note: "Rejection counts are quotable and easy to caption. Open on the number, then let the story explain it.",
          },
          {
            title: "The Price Of Their First Deal",
            note: "Money details hold attention. Keep the number and the context inside twenty seconds.",
          },
          {
            title: "What They Earn Per Project",
            note: "Guests who share real income figures produce clips that get screenshotted. Caption the figure in large type.",
          },
          {
            title: "The Age They Started And The Age It Worked",
            note: "Two numbers tell a whole story. Put both on screen as they come up.",
          },
          {
            title: "The Stat That Changed Their Mind",
            note: "A figure the guest cites as the reason for a decision. Open on the figure, close on the decision.",
          },
          {
            title: "How Long It Actually Took",
            note: "Real timelines contradict the overnight success story. Clip the moment they say the true duration.",
          },
          {
            title: "The Percentage Nobody Believes",
            note: "Any claim with a percent sign that sounds wrong will get comments asking for the source. Caption it word for word so viewers argue with the guest, not with you.",
          },
        ],
      },
      {
        id: "origin-stories-and-turning-points",
        title: "Origin stories and turning points",
        description: "Compressed arcs that resolve inside the length of a short.",
        ideas: [
          {
            title: "The Day Everything Changed",
            note: "Ask for the single day and clip the answer. Trim every aside so the arc is three beats: before, moment, after.",
          },
          {
            title: "How They Got Their First Customer",
            note: "Specific, short and relatable to anyone starting out. Start on the method, not the backstory.",
          },
          {
            title: "The Job They Had Before This",
            note: "The contrast between then and now is the hook. Caption the old job title on the first frame.",
          },
          {
            title: "The Phone Call That Started It",
            note: "Many origin stories hinge on one conversation. Clip the retelling of the call and end on what they decided.",
          },
          {
            title: "What They Did With Their First Paycheck",
            note: "A small question that shows character. Keep the answer and the reason, nothing else.",
          },
          {
            title: "The Moment They Knew It Would Work",
            note: "Find the sentence where doubt turns to certainty. Start one line before it.",
          },
          {
            title: "The Accident That Became The Business",
            note: "Unplanned origins read as honest. Open on the accident and let the pivot be the payoff.",
          },
          {
            title: "Why They Left Their Last Thing",
            note: "Departures have conflict built in. Clip the reason, not the farewell.",
          },
        ],
      },
      {
        id: "question-reaction-and-cliffhanger-hooks",
        title: "Question, reaction and cliffhanger hooks",
        description: "Openings built from the host, the room, or what is withheld.",
        ideas: [
          {
            title: "The Question The Guest Did Not Expect",
            note: "Clip the host question and the first reaction before any answer. The silence is the hook.",
          },
          {
            title: "The Guest Answers A Question From The Comments",
            note: "Match a segment to a question your audience actually asked. Title the clip with that question word for word.",
          },
          {
            title: "Wait, Say That Again",
            note: "When the host asks for a repeat, the line was worth hearing. Start on the request and keep the repeat.",
          },
          {
            title: "The Laugh That Breaks The Interview",
            note: "Genuine laughter is a reaction viewers trust. Keep both people in frame and do not trim the recovery.",
          },
          {
            title: "The Candid Aside They Agreed To Keep",
            note: "If a guest agrees to keep an off hand remark in, clip it with the permission line included. The permission is the hook.",
          },
          {
            title: "The Answer That Stops Before The End",
            note: "Cut just before the resolution and let the caption promise it. Use this only when the resolution delivers.",
          },
          {
            title: "The Prediction With A Date On It",
            note: "A forecast tied to a year is a cliffhanger that ages into repost material. Put the date on screen.",
          },
          {
            title: "The One Word Answer",
            note: "A guest who answers a big question with one word gives you a three second hook. Open on the question, land on the word, then let the full answer run.",
          },
        ],
      },
    ],
    faqs: [
      {
        question: "What is an interview clip hook?",
        answer:
          "The first line and first frame of a clip cut from an interview, chosen so a viewer who has heard none of the episode understands what is at stake. The hook is usually a sentence the guest said, not something the editor adds.",
      },
      {
        question: "How long should the hook on an interview clip be?",
        answer:
          "One sentence, delivered inside the first two seconds, with the same words on screen for viewers watching muted. If the guest takes longer to get to the point, start the cut later rather than keeping the preamble.",
      },
      {
        question: "Can I use these hooks on other people's podcasts?",
        answer:
          "Only with permission: a clipping campaign, a published clipping policy or a direct agreement with the show. The hook types work on any interview, but the footage still belongs to whoever recorded it.",
      },
      {
        question: "How do I find these moments without rewatching the whole episode?",
        answer:
          "Work from the transcript. Search for the phrases guests use to flag a confession, a number or a disagreement, then check the audio at each timestamp. Moment finding tools do the same scan automatically and surface the candidates with a suggested start point.",
      },
    ],
    related: [
      "/features/find-viral-moments",
      "/use-cases/podcast-clipping",
      "/features/ai-captions",
      "/calculators/clips-from-one-video",
    ],
  },
  {
    slug: "sports-clip-ideas",
    navTitle: "Sports Clip Ideas",
    keyword: "Sports Clip Ideas for Commentary and Fitness Channels",
    seoDescription:
      "Sports clip ideas for commentary and fitness channels, cut from your own podcasts, press conferences, streams and training footage, grouped by source.",
    intro:
      "Every idea here is cut from footage you own or are licensed to use: your commentary podcast, interviews and press conferences you recorded or hold rights to, your streams, and training sessions you filmed. None of them need broadcast game footage, which is the fastest way to lose a channel. They are grouped by source so you can work through whichever one you have this week.",
    howToPick: [
      "Work only from footage you own or have a licence for, and treat broadcast game footage as off limits.",
      "Lead with the opinion or the moment, not the context, because the viewer already knows the sport.",
      "Favour segments with a number, a name or a prediction, since those are what sports comments argue about.",
      "Cut one argument per clip, even when the debate covers three.",
    ],
    makeWith: {
      featurePath: "/features/clip-cutter",
      featureLabel: "Clip Cutter",
      createPath: "/create/clip-cutter",
    },
    sections: [
      {
        id: "hot-takes-and-debates",
        title: "Hot takes and debate segments",
        description: "Commentary podcast moments where the argument is the content.",
        ideas: [
          {
            title: "The Ranking That Will Start A Fight",
            note: "Clip the moment a host puts a player or team at a number the audience will hate. Put the ranking on screen as it is said.",
          },
          {
            title: "The Trade They Would Make Tomorrow",
            note: "Hypothetical trades are the oldest sports argument. Caption both sides so the comments can take positions without listening.",
          },
          {
            title: "The Prediction With A Score Attached",
            note: "A specific scoreline is clippable now and repostable after the game. Put the prediction and the date on screen.",
          },
          {
            title: "The Co Host Who Walks It Back",
            note: "A hot take softened under pushback is more watchable than a clean one. Clip both halves.",
          },
          {
            title: "Overrated Or Underrated In Ten Seconds",
            note: "Rapid fire verdicts produce several clips from one segment. Cut each name as its own post.",
          },
          {
            title: "The Stat That Ends The Argument",
            note: "When one host drops a number that settles the debate, open on the number. Let the other host react.",
          },
          {
            title: "The Take They Got Wrong Last Season",
            note: "Self correction is rare in sports commentary and earns respect. Clip the admission and the new position.",
          },
          {
            title: "The Rule Change They Would Make",
            note: "Everyone has one and nobody agrees. Keep the rule and the one sentence reason, nothing else.",
          },
        ],
      },
      {
        id: "press-conferences-and-interviews",
        title: "Press conferences and interviews",
        description:
          "Moments from interviews you recorded, pressers you were accredited for, or footage you hold rights to.",
        ideas: [
          {
            title: "The Question You Asked",
            note: "If you have media access, clip the exchange where you asked the question. Caption your question so the answer has context.",
          },
          {
            title: "The Non Answer",
            note: "A deflection is a story on its own. Start on the question and end on the dodge, with no commentary needed.",
          },
          {
            title: "The Coach Who Names The Problem",
            note: "Clip the moment a coach stops being diplomatic. Keep the pause before it, since that is the tell.",
          },
          {
            title: "The Athlete Reacting To Their Own Stat",
            note: "When an athlete is read a number about their performance, clip the reaction. Put the stat on screen.",
          },
          {
            title: "The Answer That Contradicts Last Week",
            note: "Line up a quote with an earlier one from your own archive. Caption the dates.",
          },
          {
            title: "The Interview Line Everyone Will Quote",
            note: "One sentence that stands alone as text. Put the words on screen so the frame screenshots.",
          },
          {
            title: "The Rookie Describing The Jump",
            note: "First year athletes describing what changed are relatable to amateur viewers. Cut the specific detail, not the cliche.",
          },
          {
            title: "Your Reaction To The Presser",
            note: "Film yourself responding to the quote instead of reposting the broadcast. Your face plus the quote as text is the whole clip.",
          },
        ],
      },
      {
        id: "breakdowns-and-analysis",
        title: "Breakdowns and tactical analysis",
        description:
          "Explain the game with diagrams, your own footage and your voice, not the broadcast feed.",
        ideas: [
          {
            title: "The Play On A Whiteboard",
            note: "Draw the play rather than showing it. The diagram is the visual and your explanation is the hook.",
          },
          {
            title: "One Stat, One Minute",
            note: "Take a single number and explain what it hides. Put the number on screen first and the explanation second.",
          },
          {
            title: "Why The Coverage Missed It",
            note: "Argue that a point the commentators glossed over mattered more. Name the moment in your caption instead of showing the footage.",
          },
          {
            title: "The Recreated Play",
            note: "Re enact a sequence with friends on a local pitch or court, filmed by you. Cut to the decisive moment.",
          },
          {
            title: "The Tactical Trend Of The Season",
            note: "Explain a shift in how teams play using diagrams and your own commentary. One trend per clip.",
          },
          {
            title: "The Mistake Amateurs Copy From Pros",
            note: "Bridge analysis and coaching. Show the right version on yourself or on a player you film.",
          },
          {
            title: "What The Box Score Does Not Show",
            note: "Walk through a performance with the stat sheet on screen. The hook is the gap between the number and the game.",
          },
          {
            title: "The Decision In The Final Minute",
            note: "Analyse a late call with a clock graphic and your voice. Say what you would have done.",
          },
        ],
      },
      {
        id: "training-and-fitness",
        title: "Training and fitness footage",
        description: "Sessions you filmed, where the progress or the technique is the story.",
        ideas: [
          {
            title: "The Rep That Failed",
            note: "A missed lift or a dropped set is more watchable than a clean one. Keep the reaction after it.",
          },
          {
            title: "Before And After In One Frame",
            note: "Side by side footage from two sessions months apart. Put the dates on screen.",
          },
          {
            title: "The Technique Fix In Thirty Seconds",
            note: "Show the error, then the correction, on the same movement. Slow motion on the fix.",
          },
          {
            title: "The Drill Nobody Does",
            note: "Film an unusual drill and explain why it works in one sentence. Name the muscle or skill it targets.",
          },
          {
            title: "The Warm Up Of A Pro",
            note: "If you train or film with an athlete who agrees to it, their routine is a clip. Caption each step as it happens.",
          },
          {
            title: "Timed Challenge With A Visible Clock",
            note: "Put a timer on screen and attempt something specific. The clock is the retention device.",
          },
          {
            title: "Coaching A Beginner On Camera",
            note: "The first attempt and the first improvement are both clips. Keep your cue audible.",
          },
          {
            title: "The Recovery Day Reality",
            note: "Show what rest actually looks like for a serious athlete. Counterprogramming to highlight reels holds attention.",
          },
        ],
      },
      {
        id: "streams-and-watch-alongs",
        title: "Streams, watch-alongs and live reactions",
        description: "Your camera, your reaction and your chat, with the broadcast kept off screen.",
        ideas: [
          {
            title: "The Reaction With The Game Off Screen",
            note: "Film your face and your chat reacting, not the feed. Caption what just happened so the clip stands alone.",
          },
          {
            title: "The Call You Made Before It Happened",
            note: "Clip the moment you predicted something on stream, then the moment it came true. One clip with a jump cut between them.",
          },
          {
            title: "Chat Disagrees With You",
            note: "Put the chat message on screen and respond to it. The argument is the content.",
          },
          {
            title: "The Live Tier List Update",
            note: "Move a team or player on your tier list on stream and explain why. The movement on screen is the hook.",
          },
          {
            title: "The Viewer Call In Hot Take",
            note: "When a viewer joins and says something strong, clip their claim and your response. Get their permission in chat first.",
          },
          {
            title: "The Co Streamer Split Reaction",
            note: "Two streamers reacting differently to the same moment. Keep both cameras in a split.",
          },
          {
            title: "The Post Game Verdict In Ten Seconds",
            note: "End each stream with a one line verdict to camera. It is the same clip every week, which is the point.",
          },
          {
            title: "The Training Stream Milestone",
            note: "If you stream workouts or practice, clip the moment a personal best lands. Keep the sound of the room.",
          },
        ],
      },
    ],
    faqs: [
      {
        question: "Can I use game highlights or broadcast footage in sports clips?",
        answer:
          "Not safely. Leagues and broadcasters hold the rights and claim or strike channels that repost their footage, which is why every idea here works from your own commentary, interviews you hold rights to, your streams and training you filmed. The opinion is yours and the footage should be too.",
      },
      {
        question: "Do I need media access to make press conference clips?",
        answer:
          "No. Reacting to a quote on camera with the words as text is a clip on its own, and interviews you record yourself are the strongest source. If you do have accreditation or a licence, clip the exchanges directly and caption the question.",
      },
      {
        question: "Which sports clip format should I start with?",
        answer:
          "Debate segments from a commentary podcast, because the recording already exists and every ranking, trade or prediction is a separate clip. Add training or stream clips once posting is a habit.",
      },
      {
        question: "How do I clip a two hour commentary episode quickly?",
        answer:
          "Scan the transcript for names, numbers and predictions, mark the timestamps, then cut each argument to start on the claim and end on the reaction. A clip cutter that works from the full recording lets you mark all the ranges first and export them in one pass.",
      },
    ],
    related: [
      "/features/clip-cutter",
      "/features/find-viral-moments",
      "/use-cases/streamers",
      "/use-cases/podcast-clipping",
    ],
  },
  {
    slug: "faceless-youtube-channel-ideas",
    legacy: true,
    navTitle: "Faceless YouTube Channel Ideas",
    keyword: "Faceless YouTube Channel Ideas That Still Work in 2026",
    seoDescription:
      "Faceless YouTube channel ideas that still work in 2026, grouped by niche, each with the angle, the format and the visuals it needs.",
    intro:
      "Every idea here is a faceless YouTube channel concept you can start this week without appearing on camera. They are grouped by niche so you can weigh earning potential against how much research each one takes. Pick one angle, script three episodes before you commit to a name, and record the narration with an AI voice.",
    howToPick: [
      "Pick a niche you can still find something new to say about after twenty episodes.",
      "Check whether the topic attracts advertisers, because finance, software and business pay far more per view than general entertainment.",
      "Favour formats where the visuals are easy to source, such as stock footage, screen capture or AI images.",
      "Test the idea with three full scripts before you buy a channel name or design a logo.",
    ],
    makeWith: {
      featurePath: "/features/ai-voiceover",
      featureLabel: "AI Voiceover",
      createPath: "/tools/voiceovers",
    },
    sections: [
      {
        id: "high-cpm-explainers",
        title: "High CPM explainers",
        description:
          "Money, business and software topics attract the advertisers who pay the most per thousand views.",
        ideas: [
          {
            title: "Debt Free Countdown",
            note: "Narrate one anonymized budget teardown per episode over a spreadsheet screen capture, counting a balance down to zero. The countdown gives viewers a reason to subscribe rather than watch once.",
          },
          {
            title: "The AI Layoffs Desk",
            note: "Cover one new tool per episode and the specific tasks it replaces. Put the job title in the title so people searching their own role find you.",
          },
          {
            title: "Boring Business Breakdown",
            note: "Break down the real numbers behind vending routes, laundromats and car washes. Show the math on screen while the voiceover walks through startup cost, margin and hours.",
          },
          {
            title: "Rich By Accident",
            note: "Tell stories of wealth that came from one unplanned decision. Open on the outcome, then rewind to the moment it hinged on.",
          },
          {
            title: "The Subscription Audit",
            note: "Add up what a stacked set of subscriptions actually costs one kind of household per year. The total is the hook, so put it in the thumbnail.",
          },
          {
            title: "AI Tool Teardown",
            note: "Give one tool one real task and deliver the verdict in the first ten seconds. Screen capture carries the visuals, so you never need footage or a camera.",
          },
          {
            title: "The Passive Income Reality Check",
            note: "Take a claim that circulates widely and check it against how the money actually works. Keep the tone neutral so it reads as analysis rather than a rant.",
          },
        ],
      },
      {
        id: "mystery-horror-true-crime",
        title: "Mystery, horror and true crime",
        description: "High retention formats where narration and pacing do all the work.",
        ideas: [
          {
            title: "Unsolved But Forgotten",
            note: "Cover cold cases that never got podcast coverage. Stick to documented reporting, leave speculation out, and end each episode on what is still unknown.",
          },
          {
            title: "The Last Text Message",
            note: "Build each story around the final known message in a case. The message on screen is the whole thumbnail, and the narration fills in what came before it.",
          },
          {
            title: "Cursed Object Archive",
            note: "Narrate one object per episode in a museum catalog voice. Slow AI images and a calm read do more here than fast cuts.",
          },
          {
            title: "Small Town Disappearance Files",
            note: "Cover only towns under five thousand people. The constraint gives the channel a recognizable identity and keeps each research cycle short.",
          },
          {
            title: "Night Shift Horror",
            note: "Collect unsettling stories from overnight jobs such as hospital security, hotel desks and long haul routes. The setting sets the mood before you say anything.",
          },
          {
            title: "The Neighborhood Nobody Talks About",
            note: "Tie each legend to a real landmark you can show on a map. Ground the episode in geography first, then let the story run.",
          },
        ],
      },
      {
        id: "documentary-and-history",
        title: "Documentary and history",
        description:
          "Archive material and stills mean you can build these without filming anything.",
        ideas: [
          {
            title: "The Company That Used To Be Everything",
            note: "One fallen giant per episode, from peak market share to the decision that started the slide. Old ads and stills carry the visuals.",
          },
          {
            title: "Banned In One Country",
            note: "Take a product, book or song and explain the single decision that got it banned somewhere. Curiosity title, short research cycle.",
          },
          {
            title: "The Map That Changed",
            note: "Show one redrawn border per episode with a simple animated map. Explain who gained, who lost and what people still argue about.",
          },
          {
            title: "Failed Futures",
            note: "Revisit predictions that never arrived, from flying cars to the paperless office. Open on the confident old quote, then cut to what happened instead.",
          },
          {
            title: "One Object, One War",
            note: "Explain a conflict through a single item such as a ration tin, a radio or a boot. The narrow frame makes heavy history watchable.",
          },
          {
            title: "The Recall Files",
            note: "Cover one product recall per episode and the failure behind it. Documents on screen give you visuals and credibility at the same time.",
          },
        ],
      },
      {
        id: "lists-and-curiosity",
        title: "Lists and curiosity",
        description: "The fastest formats to script, which makes them the easiest place to start.",
        ideas: [
          {
            title: "Ranked By How Wrong It Went",
            note: "Rank outcomes from mildly bad to catastrophic and count down. Easy to script and easy to bank several episodes ahead.",
          },
          {
            title: "The One Percent Rule",
            note: "Each episode covers one rare outcome and the odds behind it. State the odds early so viewers stay for the example.",
          },
          {
            title: "Copied and Better",
            note: "Cover copycat products that outsold the original. Two logos side by side gives you the thumbnail before you write a word.",
          },
          {
            title: "What Is Illegal In Your State",
            note: "Work through obscure local laws one region at a time. Regional titles pull steady search traffic and locals fill the comments.",
          },
          {
            title: "The Job That No Longer Exists",
            note: "Profile a role technology erased, such as switchboard operator or lamplighter. Close each episode with what replaced it.",
          },
        ],
      },
      {
        id: "wellness-and-self-improvement",
        title: "Wellness and self improvement",
        description: "Calm narration formats that reward long watch times and repeat viewers.",
        ideas: [
          {
            title: "The 2am Thoughts Channel",
            note: "Calm narration over slow AI visuals for people awake at night. Keep the pacing slow, the music low and the episodes long.",
          },
          {
            title: "Undoing a Bad Habit",
            note: "Run a thirty day case study per habit with a fixed check in structure. The series format keeps people subscribed for the ending.",
          },
          {
            title: "Therapy Speak Explained",
            note: "Define one term per episode, such as gaslighting or love bombing, with a plain example. Say clearly that it is education rather than diagnosis.",
          },
          {
            title: "The Burnout Autopsy",
            note: "Walk through how one career path wears people down, stage by stage. Anonymize every detail and keep the focus on the pattern.",
          },
          {
            title: "Small Discipline Wins",
            note: "Short episodes on one tiny habit that compounds. Low production cost, high posting frequency, and each one repurposes into a Short.",
          },
        ],
      },
      {
        id: "nostalgia-and-pop-culture",
        title: "Nostalgia and pop culture",
        description: "Comment driven formats where the audience does half the promotion.",
        ideas: [
          {
            title: "The Show That Got Cancelled Too Soon",
            note: "One cancelled series per episode, what it was building toward and why it stopped. Expect the comments to argue, and let them.",
          },
          {
            title: "Remember When The Internet Was Different",
            note: "Revisit an old platform, layout or online ritual. Screenshots and archived pages are all the footage you need.",
          },
          {
            title: "One-Hit Wonder Autopsy",
            note: "Trace what happened to an artist after their one big song. End with where they are now so the loop closes.",
          },
          {
            title: "The Toy Every 90s Kid Wanted",
            note: "Cover one toy per episode, the ad campaign behind it and what it sells for now. The resale price is the payoff.",
          },
        ],
      },
      {
        id: "niche-how-to",
        title: "Niche how to",
        description: "Search driven formats that keep earning long after the upload date.",
        ideas: [
          {
            title: "The Contract Nobody Reads",
            note: "Explain one clause per episode in plain language, from leases to gym memberships. Add a line saying it is general information rather than legal advice.",
          },
          {
            title: "Fixed In Five Minutes",
            note: "One common household or device fix per episode, timed and shown on screen. Search traffic keeps these alive for years.",
          },
          {
            title: "The Interview Question Breakdown",
            note: "Take one question, three answer patterns and what each one signals. Keep it under four minutes so people can rewatch it before an interview.",
          },
          {
            title: "Kitchen Science",
            note: "Explain why one cooking step works, such as resting meat or salting the water. Close up stock footage plus narration is the entire format.",
          },
          {
            title: "The App You Are Using Wrong",
            note: "Show one hidden setting or workflow per episode with screen capture. Put the app name in the title so the search intent is obvious.",
          },
        ],
      },
    ],
    faqs: [
      {
        question: "Do faceless YouTube channels actually make money?",
        answer:
          "Yes, through the same ad revenue, sponsorship and affiliate paths as any other channel. What changes is the rate. Finance, software and business topics attract advertisers who pay far more per thousand views than general entertainment, so the niche matters more than whether you show your face.",
      },
      {
        question: "Which faceless niche is easiest to start with?",
        answer:
          "Lists and curiosity formats. The research is shallow, the scripts are repeatable and you can bank a week of episodes in one sitting. The tradeoff is a lower CPM, so many creators start there to build the habit and move toward higher paying topics once they can post consistently.",
      },
      {
        question: "Do I need to show my face at any point?",
        answer:
          "No. Every idea on this page works with narration over stock footage, screen capture, AI images or simple motion graphics. Plenty of channels run for years without a single on camera appearance, and the audience follows the format rather than a person.",
      },
      {
        question: "What if I am not a strong writer?",
        answer:
          "A specific angle beats polished prose. Decide what the episode proves before you write a line, keep sentences short enough to say out loud, then let an AI voiceover read the script back so awkward phrasing shows up while you can still fix it.",
      },
    ],
    related: [
      "/features/ai-voiceover",
      "/use-cases/faceless-youtube-channels",
      "/calculators/creator-money-calculator",
      "/calculators/cpm-rpm-calculator",
    ],
  },
  {
    slug: "reddit-story-ideas",
    legacy: true,
    navTitle: "Reddit Story Ideas",
    keyword: "Reddit Story Ideas for Faceless Story Channels",
    seoDescription:
      "Original reddit story ideas for faceless story channels, grouped by drama type, each with the hook, the escalation and where to cut for part two.",
    intro:
      "These are original story premises written for reddit style narration videos, not copied posts. Each one gives you a conflict, a turn and a place to cut for part two. They are grouped by the drama types that hold attention best, so pick a premise, write the escalation in your own words, and narrate it with an AI voice.",
    howToPick: [
      "Choose a premise where the conflict is clear in the first sentence, because that sentence is your hook.",
      "Look for a stake the viewer can picture holding themselves, such as money, a wedding or a home.",
      "Keep one turn in reserve so you have somewhere to cut for part two.",
      "Write the story in your own words rather than lifting a real post, so the script stays yours.",
    ],
    makeWith: {
      featurePath: "/use-cases/faceless-youtube-channels",
      featureLabel: "Faceless Channels",
      createPath: "/create/reddit-video",
    },
    sections: [
      {
        id: "aita-relationship",
        title: "Relationship judgment calls",
        description:
          "Premises where the audience genuinely splits, which is what fills a comment section.",
        ideas: [
          {
            title: "The Wedding Seating Chart That Ended a Friendship",
            note: "A seating decision meant to avoid one awkward pairing gets read as a public ranking of friends. Open on the group chat reaction, then rewind to how the chart was built.",
          },
          {
            title: "I Told My Partner Their Best Friend Confessed Feelings For Me",
            note: "The confession happened years ago and was never repeated. The question is not whether it happened but whether saying it now was fair.",
          },
          {
            title: "My Partner Wants Me To Go Back To The Nickname My Ex Gave Me",
            note: "A harmless sounding request carries a history the partner does not know about. Reveal where the nickname came from at the halfway point.",
          },
          {
            title: "I Canceled The Honeymoon After Finding One Text",
            note: "The text is ambiguous enough that viewers take sides. Read it out twice, once flat and once with the context, and let the argument run.",
          },
        ],
      },
      {
        id: "weddings-and-family-events",
        title: "Weddings and family events",
        description: "High stakes days where one small decision escalates fast.",
        ideas: [
          {
            title: "The Bridesmaid Dress Refund Fight",
            note: "A bridesmaid drops out after paying for a dress that cannot be returned. Escalate through the group chat, then land on who ends up out of pocket.",
          },
          {
            title: "My Mother In Law Wore White To My Wedding On Purpose",
            note: "The proof turns up after the wedding rather than during it. End part one on the moment she walks in and save the evidence for part two.",
          },
          {
            title: "I Uninvited My Brother From My Wedding Over One Rule",
            note: "The rule is small and the reaction is not. Make the rule sound completely reasonable first, then show why he refused it.",
          },
          {
            title: "The Wedding Gift Return Demand",
            note: "A guest asks for the gift back after the marriage ends inside a year. Play both sides seriously so the comments argue instead of agreeing.",
          },
        ],
      },
      {
        id: "inheritance-and-estates",
        title: "Inheritance and estates",
        description: "Money plus grief, which is the most reliable escalation in the format.",
        ideas: [
          {
            title: "My Father Left The House To The Sibling Who Never Visited",
            note: "A private letter explains the decision and only one person has read it. End part one on the letter being handed over.",
          },
          {
            title: "I Found Out My Inheritance Was Already Spent",
            note: "Power of attorney turns a grief story into a paper trail story. Walk through the statements in the order the narrator discovered them.",
          },
          {
            title: "The Points System My Uncle Used To Divide His Estate",
            note: "Relatives scored on visits, calls and favours in a spreadsheet nobody knew existed. The scoring rules are the payoff, so read them out slowly.",
          },
          {
            title: "I Refused To Split A Lottery Win With My Family",
            note: "Keep the amount modest so the fight stays about principle rather than the number. The refusal is the hook and the fallout is the story.",
          },
        ],
      },
      {
        id: "workplace-and-petty-revenge",
        title: "Workplace and petty revenge",
        description: "Compliance stories with a clean setup, escalation and payoff.",
        ideas: [
          {
            title: "I Followed Every Rule In My Contract Until They Regretted It",
            note: "Malicious compliance with a clear beginning, middle and payoff. Number the rules on screen so viewers can track the escalation.",
          },
          {
            title: "My Coworker Took Credit For My Idea In A Meeting",
            note: "The narrator has a timestamped paper trail and waits to use it. The waiting is the tension, so do not release it early.",
          },
          {
            title: "I Got Promoted Over My Manager By Following Policy To The Letter",
            note: "Every step is technically correct, which is what makes it satisfying. Show the exact policy line that made it possible.",
          },
          {
            title: "The Group Project Grade Dispute",
            note: "One student logs every missed deadline and submits the log with the project. End on what the professor decides to do with it.",
          },
        ],
      },
      {
        id: "roommates-and-neighbors",
        title: "Roommates and neighbors",
        description: "Small domestic conflicts that turn on a single reveal.",
        ideas: [
          {
            title: "My Roommate Ate My Labeled Food For Six Months",
            note: "The reason reframes the whole story from petty to sad. Hold it until the last thirty seconds and do not hint at it earlier.",
          },
          {
            title: "The Neighbor Who Reported My Dog For Barking At 3am",
            note: "The dog died two years earlier, so the complaint is about something else entirely. Reveal the date as the turn.",
          },
          {
            title: "I Installed A Camera After Things Kept Disappearing From My Porch",
            note: "The footage shows someone the narrator recognizes. Describe the clip frame by frame instead of summarizing what it showed.",
          },
          {
            title: "My Landlord Tried To Keep My Deposit Over A Rule That Is Not In The Lease",
            note: "The narrator reads the lease more carefully than the landlord did. End on the single line that settles it.",
          },
        ],
      },
      {
        id: "friendship-and-betrayal",
        title: "Friendship and betrayal",
        description: "Slower burns that work best split across two parts.",
        ideas: [
          {
            title: "My Best Friend Used My Idea To Start Her Own Business Without Me",
            note: "Old messages show the idea was shared in confidence. The screenshots are the evidence beat, so build toward them rather than opening on them.",
          },
          {
            title: "I Was Not Invited To The Group Trip I Paid A Deposit For",
            note: "The deposit is the hook because it makes the exclusion concrete. Open on the booking confirmation email.",
          },
          {
            title: "My Friend Asked Me To Lie To Her Husband And I Said No",
            note: "A clean moral choice with an expensive cost. Keep the lie itself small so the fallout feels wildly out of proportion.",
          },
          {
            title: "The Friend Who Only Texts When She Needs Something",
            note: "The narrator keeps a tally for a year before saying anything. Reading the tally out loud is the entire second half.",
          },
        ],
      },
      {
        id: "lending-and-borrowing",
        title: "Lending and borrowing",
        description: "Money between people who were never going to write it down.",
        ideas: [
          {
            title: "I Cosigned A Car Loan And Then Stopped Getting Replies",
            note: "Payments land on the narrator while the car stays with someone else. Escalate through each missed payment notice in order.",
          },
          {
            title: "My Sister Borrowed My Car For A Weekend And Returned It In March",
            note: "Small favour, long overrun, mounting evidence. Track it week by week so the absurdity builds instead of being stated.",
          },
          {
            title: "I Asked For My Two Hundred Dollars Back And Became The Villain",
            note: "A small amount works better than a large one here, because the reaction is what people argue about.",
          },
          {
            title: "The Wedding Fund I Was Told Was A Gift",
            note: "Years later it is described as a loan with interest attached. End part one the first time the word loan appears.",
          },
        ],
      },
      {
        id: "parenting-and-boundaries",
        title: "Family boundaries",
        description: "Premises about the rule that only ever applies to one person.",
        ideas: [
          {
            title: "My Parents Gave My Childhood Room To My Cousin Without Asking",
            note: "Boxes appear in the garage before anyone mentions it. The phone call where they explain is the centerpiece of the story.",
          },
          {
            title: "I Stopped Being The Free Babysitter And The Family Group Chat Exploded",
            note: "One declined weekend turns into a referendum on the narrator. Read the group chat messages in the order they arrived.",
          },
          {
            title: "My In Laws Booked A Holiday In Our House Without Telling Us",
            note: "The dates arrive as a forwarded confirmation with no question attached. Play the whole thing as escalating politeness.",
          },
          {
            title: "The Family Rule That Only Applies To Me",
            note: "The narrator finally asks why out loud at a dinner table. Everything before that is setup, so keep it tight.",
          },
        ],
      },
    ],
    faqs: [
      {
        question: "Can I use real reddit posts in my videos?",
        answer:
          "Posts belong to the people who wrote them, and reading one word for word puts you in a gray area with both the platform and the author. Writing original premises in the same style avoids the problem and lets you control the pacing, the reveal and where the cliffhanger falls.",
      },
      {
        question: "What makes a story video get watched to the end?",
        answer:
          "A first line that states the conflict, an escalation every twenty to thirty seconds, captions people can read with the sound off, and a cut that lands mid turn so part two feels necessary. Stories that explain the background first tend to lose viewers before the conflict arrives.",
      },
      {
        question: "How long should a reddit style story video be?",
        answer:
          "Split most premises into two or three parts of forty to ninety seconds each. One long read gives viewers a place to leave, while parts give them a reason to come back and give you several posts out of a single script.",
      },
      {
        question: "What should play behind the narration?",
        answer:
          "Something repetitive and low distraction that never competes with the words. Keep the captions large and centered, keep the footage muted, and change clips rarely enough that nobody starts watching the background instead of listening.",
      },
    ],
    related: [
      "/use-cases/faceless-youtube-channels",
      "/features/ai-voiceover",
      "/features/fake-texts",
      "/calculators/views-to-money",
    ],
  },
  {
    slug: "fake-text-story-ideas",
    legacy: true,
    navTitle: "Fake Text Story Ideas",
    keyword: "Fake Text Story Ideas With a Twist Ending",
    seoDescription:
      "Fake text story ideas with a twist ending, grouped by theme, each with the opening message, the reveals in the middle and the line that flips it.",
    intro:
      "Each idea here is a fake text conversation built to land on a twist. You get the opening message that raises the question, the reveals that keep people reading, and the final line that reframes everything before it. The characters are fictional, so invent the names and details, then build the thread as a chat video.",
    howToPick: [
      "Pick a premise where the first message alone makes someone want to see the second one.",
      "Make sure the twist is set up by something visible earlier in the thread rather than invented at the end.",
      "Keep the cast to two people unless the group chat itself is the story.",
      "Choose a premise you can tell in three to five reveals, since the whole thread should run under a minute.",
    ],
    makeWith: {
      featurePath: "/features/fake-texts",
      featureLabel: "Fake Text Chat",
      createPath: "/create/text-video",
    },
    sections: [
      {
        id: "cheating-and-betrayal",
        title: "Cheating and betrayal",
        description: "Threads where the accusation and the truth are not the same thing.",
        ideas: [
          {
            title: "You Were Not At The Party, Were You?",
            note: "The alibi arrives from a phone the other character insists was broken and boxed up months ago. The twist is who is actually holding it.",
          },
          {
            title: "Wrong Number, Right Photo",
            note: "A stranger sends a photo to the wrong number and the recipient recognizes the room it was taken in. Save the detail on the wall for the last message.",
          },
          {
            title: "I Found Your Other Phone",
            note: "Every reply makes it look worse until it turns out the phone belongs to a sibling in trouble. Keep the accusations short so the correction lands hard.",
          },
          {
            title: "The Location Share Nobody Turned Off",
            note: "One character thinks the share was forgotten. It was left on deliberately as a test, and the final message says so plainly.",
          },
        ],
      },
      {
        id: "group-chat-mysteries",
        title: "Group chat mysteries",
        description:
          "Multi person threads where the person leading the search is usually the answer.",
        ideas: [
          {
            title: "Do Not Trust Anyone In This Group Chat",
            note: "A warning arrives from an unknown member and the group starts naming suspects. The tip came from inside the group, which the last message confirms.",
          },
          {
            title: "Someone Screenshotted This Chat And Sent It To Her",
            note: "The person leading the hunt for the leaker is the leaker. Plant the tell in an early message that only makes sense on a second watch.",
          },
          {
            title: "New Member Added: Unknown",
            note: "Nobody admits to adding the number and the chat gets careful. It is a parent reading along, given away by one very specific question.",
          },
          {
            title: "Why Does Everyone Keep Leaving The Chat?",
            note: "Members exit one by one after being messaged privately. The last person left finally finds out what they were all told.",
          },
        ],
      },
      {
        id: "family-secrets",
        title: "Family secrets",
        description: "Slower threads where the reveal changes the family rather than the plot.",
        ideas: [
          {
            title: "Mom, We Need To Talk About Dad's Old Phone",
            note: "A charged phone turns up in a drawer with a second set of contacts. Deliver the reveal through what the contacts are named rather than a stated confession.",
          },
          {
            title: "I Found Adoption Papers In The Attic",
            note: "The reader assumes the papers are about the narrator. They are about a sibling who does not know, and the thread ends on whether to tell them.",
          },
          {
            title: "Grandma Left A Voicemail Before She Passed",
            note: "Two relatives transcribe it line by line in the thread. The last line names who gets the house.",
          },
          {
            title: "Why Is Dad's Number Saved As Do Not Answer?",
            note: "A sibling set the label years earlier and never explained it. The reason is the twist, and it is protective rather than sinister.",
          },
        ],
      },
      {
        id: "situationship-and-breakup",
        title: "Situationships and breakups",
        description: "Threads that read as ordinary until one timestamp changes the meaning.",
        ideas: [
          {
            title: "It Started As A Joke To Make My Ex Jealous",
            note: "Two friends fake a relationship in messages meant to be seen. One of them stops pretending, and the timestamps show exactly when.",
          },
          {
            title: "He Texted We Need To Talk Then Left Me On Read For Three Days",
            note: "Three days of increasingly hurt messages, then a reply from a hospital. Do not soften the earlier messages, because the contrast is the point.",
          },
          {
            title: "She Blocked Me Everywhere Except One App She Forgot About",
            note: "A single open channel becomes the entire conversation. The twist is that she did not forget about it at all.",
          },
          {
            title: "I Matched With My Ex's New Partner By Accident",
            note: "Neither person realizes the connection until a detail slips out. Let the reader work it out one message before the characters do.",
          },
        ],
      },
      {
        id: "scam-and-stranger",
        title: "Scams and strangers",
        description: "Threads that use the reader assumption about scams against them.",
        ideas: [
          {
            title: "This Is The IRS. Since When Does The IRS Know My Kid's Nickname?",
            note: "A generic scam thread turns specific, which is far more unsettling than any threat. The nickname came from a data breach, revealed at the end.",
          },
          {
            title: "I Replied To A Wrong Number Text As A Joke",
            note: "The narrator plays along as someone else and the joke gets serious. End the thread the moment the other person asks to meet.",
          },
          {
            title: "Your Package Could Not Be Delivered",
            note: "The reader assumes a scam, and the twist is that the delivery problem is real and urgent. Subverting the obvious read is what makes it share.",
          },
          {
            title: "A Number I Have Never Seen Sent My Own Address Back To Me",
            note: "Play it as horror for four messages, then resolve it as a delivery mix up. The relief beat is the payoff.",
          },
        ],
      },
      {
        id: "supernatural",
        title: "Supernatural and unexplained",
        description:
          "Threads where every rational explanation gets ruled out before the real one lands.",
        ideas: [
          {
            title: "Texts From A Phone That Was Turned Off Two Years Ago",
            note: "Rule out each explanation in turn until only one is left. A sibling kept the line active and never said why.",
          },
          {
            title: "I Keep Getting Texts Signed With My Own Name",
            note: "Messages arrive in the narrator's voice with details only they know. It turns out to be a scheduled prank they set up and forgot about.",
          },
          {
            title: "Answer If You Can Hear Me",
            note: "A one sided thread that seems to be reaching someone who is gone. The last message reveals a ghost tour app replying automatically.",
          },
          {
            title: "The Contact Saved As Do Not Delete Just Texted Me",
            note: "An old reminder system fires years later than intended. Build dread across four messages, then let the explanation land quietly.",
          },
        ],
      },
      {
        id: "workplace-threads",
        title: "Workplace threads",
        description: "Office chat premises where the tension is entirely about who knows what.",
        ideas: [
          {
            title: "Do Not Reply To The Email I Just Sent",
            note: "A message went to the wrong recipient list and there is a minute to fix it. The twist is that the wrong list was the right one all along.",
          },
          {
            title: "Why Is My Badge Not Working This Morning?",
            note: "A colleague answers vaguely while the narrator stands outside the building. The real reason arrives as a forwarded message near the end.",
          },
          {
            title: "I Read The Meeting Notes You Were Not Supposed To Send Me",
            note: "Two coworkers negotiate what to do about it. The twist is that the notes were sent on purpose.",
          },
          {
            title: "Are You Also Getting The Calendar Invite For Tomorrow?",
            note: "Half the team has it and half does not, and the split says everything. Let the reader count the names before anyone spells it out.",
          },
        ],
      },
      {
        id: "school-and-roommate",
        title: "School and roommate chats",
        description: "Low stakes premises that work because the reaction is out of proportion.",
        ideas: [
          {
            title: "Whoever Is Using My Laundry Card, We Need To Talk",
            note: "A building group chat turns into an investigation over something trivial. The culprit is the person organizing the search.",
          },
          {
            title: "The Study Group Chat I Was Removed From",
            note: "A classmate forwards a screenshot of what was said after the removal. Cut before the final message, then reveal it.",
          },
          {
            title: "Someone Signed Me Up For Every Newsletter On The Internet",
            note: "The narrator narrows it to three suspects inside the thread. The confession is not the funny part, the reason is.",
          },
          {
            title: "My Roommate Left A Note Instead Of Answering My Texts",
            note: "The thread and the note contradict each other. Show the note as an image beat in the middle of the conversation.",
          },
        ],
      },
    ],
    faqs: [
      {
        question: "Are fake text videos real conversations?",
        answer:
          "No. They are simulated chat threads written as fiction. Nothing is sent or received, so treat every message as a script line and invent the names, numbers and details rather than reusing anything real.",
      },
      {
        question: "Can I base a fake text story on someone I know?",
        answer:
          "Write the characters as fictional and strip out anything identifying, including names, workplaces, locations and dates. A premise loosely inspired by real life is fine. A recognizable portrait of a real person is not, and it is the fastest way to get a video reported.",
      },
      {
        question: "How long should a fake text video be?",
        answer:
          "Aim for three to five reveals plus the twist, which usually lands between thirty and sixty seconds. Longer threads lose people in the middle, and shorter ones do not give the ending enough to reframe.",
      },
      {
        question: "How do I keep people reading to the twist?",
        answer:
          "Make the first message a question the reader wants answered, then have every reply change what they think is happening. Time the message reveals to reading speed, and keep the final line short enough to take in at a glance.",
      },
    ],
    related: [
      "/features/fake-texts",
      "/use-cases/faceless-youtube-channels",
      "/features/ai-voiceover",
      "/calculators/posts-to-a-million-views",
    ],
  },
  {
    slug: "youtube-shorts-ideas",
    navTitle: "YouTube Shorts Ideas",
    keyword: "YouTube Shorts Ideas for Any Channel",
    seoDescription:
      "YouTube Shorts ideas for any channel, grouped by format, each with the shot, the hook and the edit so you can post something today.",
    intro:
      "These Shorts formats work on almost any channel, so you can pick one and post today instead of waiting for a concept to arrive. Each idea names the shot, the hook and the edit. If you already have long videos, the repurposing section turns a single upload into a week of Shorts.",
    howToPick: [
      "Pick a format you can shoot with what is already in the room.",
      "Choose an idea whose payoff is visible in the first two seconds rather than explained in the last five.",
      "Prefer formats that point viewers back toward something longer on your channel.",
      "If you are stuck, start with a repurposing format, because the footage already exists.",
    ],
    makeWith: {
      featurePath: "/features/auto-clip",
      featureLabel: "Auto Clip",
      createPath: "/create/auto-clip",
    },
    sections: [
      {
        id: "pov-and-transformation",
        title: "POV and transformation",
        description: "Formats where the visual carries the hook without a spoken setup.",
        ideas: [
          {
            title: "The Before You Scroll Transformation",
            note: "Show the finished result in the first frame, then rewind to the start. Viewers stay to see how it got there instead of waiting to find out whether it is worth it.",
          },
          {
            title: "POV: You Just Found Out",
            note: "Hold a single reaction shot while the on screen text delivers the news. It works in any niche because the text carries all the specificity.",
          },
          {
            title: "The One Take Room Reveal",
            note: "Walk a space in one unbroken shot with no cuts at all. The absence of editing is the format, so resist adding any.",
          },
          {
            title: "Then Versus Now, Same Angle",
            note: "Match the framing of an old photo or clip exactly and cut between them. Line up the horizon before you shoot, because the match is the whole effect.",
          },
        ],
      },
      {
        id: "educational-micro",
        title: "Educational micro formats",
        description: "Save and share formats that keep pulling search traffic.",
        ideas: [
          {
            title: "The Thirty Second Fix",
            note: "One problem, one fix, timed on screen. Say what breaks before you say how to fix it so people know whether it applies to them.",
          },
          {
            title: "Wrong Way Versus Right Way",
            note: "Show the mistake for three seconds, then the correction. A split screen keeps both visible so the comparison does the explaining.",
          },
          {
            title: "One Word You Are Using Wrong",
            note: "Correct a single common misuse and give the right one in a sentence. Short enough to rewatch and specific enough to save.",
          },
          {
            title: "The Setting Nobody Changes But Should",
            note: "Screen record one toggle and what changes after it. Name the app in the first line so the right people stop scrolling.",
          },
        ],
      },
      {
        id: "trend-remix",
        title: "Trend remixes",
        description:
          "Formats built to attract comments, duets and replies rather than watch time alone.",
        ideas: [
          {
            title: "The Sound On Reveal",
            note: "Build the edit so the audio moment lands at the same instant as the visual. Ask for sound with on screen text, not with narration.",
          },
          {
            title: "Rating Things On A Scale Of One To Ten",
            note: "Rate five items fast with the number on screen for each. Put your most arguable rating last so the comments have somewhere to go.",
          },
          {
            title: "Guess Before I Tell You",
            note: "Pose the question, hold a two second gap, then answer. The gap is what generates the guesses in the comments.",
          },
          {
            title: "The Duet Ready Take",
            note: "Leave deliberate space in the frame and end on an open question. You are making a prompt for other people, not a finished piece.",
          },
        ],
      },
      {
        id: "lists-and-rankings",
        title: "Lists and rankings",
        description: "The fastest thing to script when you need to post and have no concept.",
        ideas: [
          {
            title: "Three Things I Wish I Knew Earlier",
            note: "Three items, no intro, no outro. Number them on screen so viewers can see how far in they are.",
          },
          {
            title: "Worst To Best, No Filler",
            note: "Rank four things and cut every transition sentence between them. The pace is the format.",
          },
          {
            title: "The Cheapest Versus The Most Expensive",
            note: "Compare two extremes of the same category and end with which you would actually buy. The verdict is the reason to stay.",
          },
          {
            title: "Red Flags To Watch For",
            note: "List warning signs in one specific situation, one per beat. Keep them concrete enough to be useful rather than generic advice.",
          },
        ],
      },
      {
        id: "story-hooks",
        title: "Story hooks",
        description: "Talking formats that open mid event rather than with an introduction.",
        ideas: [
          {
            title: "The Text I Got That Changed My Plans",
            note: "Show the message on screen while you talk over it. Blur anything identifying and keep the read under fifteen seconds.",
          },
          {
            title: "I Did Not Expect This To Happen",
            note: "Start with the camera already running and the event already underway. Skip the setup and explain as you go.",
          },
          {
            title: "The Comment That Made Me Rethink Something",
            note: "Screenshot the comment, react to it, then give your revised position. Works as a follow up to any post that got argued about.",
          },
          {
            title: "What They Do Not Tell You About It",
            note: "Take a widely repeated piece of advice and add the missing caveat. Say the caveat first, then justify it.",
          },
        ],
      },
      {
        id: "repurposing-long-video",
        title: "Repurposing long video",
        description: "One upload, several posts, no new footage.",
        ideas: [
          {
            title: "The Most Controversial Minute",
            note: "Pull the segment where you said the most arguable thing. Cut in on the sentence itself with no lead in at all.",
          },
          {
            title: "The Unexpected Fact From The Episode",
            note: "Take one surprising detail out of a long upload and give it a title of its own. The fact belongs in the first three seconds.",
          },
          {
            title: "The Short That Sends Them To The Full Video",
            note: "End on the question the long video answers and pin the link in the comments. Do not answer it in the Short.",
          },
          {
            title: "Three Shorts From One Long Video",
            note: "Split a single upload into a hook clip, a payoff clip and a lesson clip, then post them across three days.",
          },
        ],
      },
      {
        id: "behind-the-scenes",
        title: "Behind the scenes",
        description: "Low effort formats that often outperform the polished version.",
        ideas: [
          {
            title: "The Take That Did Not Make It",
            note: "Post the outtake with the reason it failed on screen. Cheap to produce and it frequently beats the finished cut.",
          },
          {
            title: "What This Setup Actually Costs",
            note: "Pan across your gear with prices on screen and the total at the end. Put the total in the caption so search picks it up.",
          },
          {
            title: "The Part Everyone Thinks Is Easy",
            note: "Show the unglamorous step nobody films, in real time. The lack of a cut is what makes it believable.",
          },
          {
            title: "One Minute Of How This Gets Made",
            note: "Compress your process into a single pass with no explanation. Let the visuals carry it and put the detail in the caption.",
          },
        ],
      },
      {
        id: "audience-driven",
        title: "Audience driven",
        description: "Posts built from things your existing viewers already said.",
        ideas: [
          {
            title: "Answering The Question I Get Every Week",
            note: "Put the question on screen and answer it in one take. Pin the Short so new viewers stop asking it.",
          },
          {
            title: "You Picked It, So Here It Is",
            note: "Follow through on a poll or a comment vote in public. Show the result first so the payoff feels earned.",
          },
          {
            title: "Reacting To My Own First Video",
            note: "Play a clip of your earliest upload and mark what you would change now. Nostalgia plus self criticism keeps people to the end.",
          },
          {
            title: "The Thing You All Got Wrong In The Comments",
            note: "Correct one widespread misreading without naming anyone. Lead with the correction, then show why it spread.",
          },
        ],
      },
    ],
    faqs: [
      {
        question: "How long can a YouTube Short be?",
        answer:
          "Vertical uploads can run up to three minutes since late 2024, though the strongest performers usually sit between fifteen and thirty seconds. Use the extra room only when the story needs it, because retention still falls off on anything that stalls in the middle.",
      },
      {
        question: "How many subscribers do I need to monetize Shorts?",
        answer:
          "As of 2026 the main threshold is 1,000 subscribers plus either 10 million Shorts views in 90 days or 4,000 public watch hours in a year, with a 500 subscriber entry tier for fan funding. YouTube has said these requirements rise in 2027, so check the current numbers before planning around them.",
      },
      {
        question: "Should my Shorts match my long form niche?",
        answer:
          "Mostly yes. Shorts that share a topic with your long videos funnel viewers toward them, and the channel reads as one thing to both people and recommendations. Occasional off topic experiments are fine, but building the whole channel on them makes the audience harder to convert.",
      },
      {
        question: "Can I turn one long video into several Shorts?",
        answer:
          "Yes, and it is usually the fastest way to post consistently. Pull the moments where the energy changes, reframe them vertically, add captions and give each one a title that stands on its own without the full video for context.",
      },
    ],
    related: [
      "/features/auto-clip",
      "/use-cases/clippers",
      "/calculators/clips-from-one-video",
      "/calculators/posting-consistency-calculator",
    ],
  },
  {
    slug: "podcast-clip-ideas",
    navTitle: "Podcast Clip Ideas",
    keyword: "Podcast Clip Ideas to Pull From Every Episode",
    seoDescription:
      "Podcast clip ideas you can pull from every episode, grouped by moment type, each with what to listen for and where to start the cut.",
    intro:
      "This is a list of moment types to look for when you scan an episode for clips. Instead of guessing, you work through the categories and mark the timestamps that match. Each entry says what to listen for, where to start the cut, and why the moment holds attention with no context around it.",
    howToPick: [
      "Look for moments that make sense to someone who has heard none of the episode.",
      "Start the cut on the sentence that creates the tension, not on the question that set it up.",
      "Favour moments where something changes in the room, such as a pause, a laugh or a raised voice.",
      "Cut one idea per clip, even when two good ones sit next to each other.",
    ],
    makeWith: {
      featurePath: "/features/auto-clip",
      featureLabel: "Auto Clip",
      createPath: "/create/auto-clip",
    },
    sections: [
      {
        id: "disagreement-and-debate",
        title: "Disagreement and debate",
        description: "The moments where the conversation stops being polite.",
        ideas: [
          {
            title: "The Point Where They Actually Disagree",
            note: "Find the sentence where agreement breaks and start there. Skip the polite lead in, because the disagreement itself is what people stay for.",
          },
          {
            title: "The Interruption That Changes The Direction",
            note: "Clip the moment one person cuts in and the conversation turns. Leave the overlapping audio in rather than cleaning it up.",
          },
          {
            title: "The Pushback Nobody Expected",
            note: "A host challenges a guest who is used to being agreed with. Start on the challenge and end on the first honest sentence of the answer.",
          },
        ],
      },
      {
        id: "vulnerable-moments",
        title: "Vulnerable moments",
        description: "Delivery changes signal these before the words do.",
        ideas: [
          {
            title: "The Voice Crack Moment",
            note: "Clip the point where the delivery breaks. Leave the silence after it intact, because trimming it removes the reason the clip works.",
          },
          {
            title: "The Thing They Said They Would Not Usually Admit",
            note: "Guests often flag it themselves with a phrase like I have never said this before. Start the cut on that phrase.",
          },
          {
            title: "The Pause Before The Real Answer",
            note: "A long pause means the guest is choosing words carefully. Keep the pause in, since it is the tension the clip runs on.",
          },
        ],
      },
      {
        id: "hot-takes",
        title: "Hot takes and predictions",
        description: "Claims that give the comments something to fight about.",
        ideas: [
          {
            title: "The Take That Will Get Comments",
            note: "Find the claim you would argue with and clip it unedited. Put the claim in the caption so the argument starts before playback does.",
          },
          {
            title: "Everyone Believes This. This Guest Does Not",
            note: "Set up the consensus with one caption line, then let the guest contradict it. The setup should take no more than two seconds.",
          },
          {
            title: "The Prediction They Are Willing To Bet On",
            note: "Clip a specific forecast with a date attached to it. Datable predictions age into repost material later.",
          },
        ],
      },
      {
        id: "storytelling",
        title: "Storytelling",
        description: "Anecdotes that resolve inside the length of a short.",
        ideas: [
          {
            title: "The Story With A Clear Ending",
            note: "Look for anecdotes that resolve inside sixty seconds. If the ending needs context from earlier in the episode, pick a different story.",
          },
          {
            title: "The You Will Not Believe What Happened Next Segment",
            note: "Cut just before the resolution and let the caption promise it. Only use this when the resolution actually delivers.",
          },
          {
            title: "The Origin Story In Sixty Seconds",
            note: "Compress how the guest started into one tight clip. Trim every aside, since the arc matters more than the detail.",
          },
        ],
      },
      {
        id: "advice-and-takeaways",
        title: "Advice and takeaways",
        description: "Lines people screenshot rather than rewatch.",
        ideas: [
          {
            title: "The One Piece Of Advice Worth Screenshotting",
            note: "Clip a line that stands alone as text. Put the exact words on screen so people can screenshot the frame.",
          },
          {
            title: "The Mistake They Tell Everyone To Avoid",
            note: "Find the warning the guest repeats in different words across the episode. Take the clearest phrasing and cut everything around it.",
          },
          {
            title: "The Answer To A Question Your Audience Actually Asks",
            note: "Match a segment to a question that keeps showing up in your comments. Title the clip with the question word for word.",
          },
        ],
      },
      {
        id: "credibility-and-reactions",
        title: "Credibility and reactions",
        description: "Moments that tell a new viewer why this conversation matters.",
        ideas: [
          {
            title: "The Number That Surprises People",
            note: "Any concrete figure the guest drops can anchor a clip. Put the number on screen at the moment it is said.",
          },
          {
            title: "The Credential Drop",
            note: "Clip the moment the guest reveals what qualifies them to say this. Useful early in a launch when nobody knows who they are yet.",
          },
          {
            title: "The Moment The Host Reacts Genuinely",
            note: "Unscripted reactions signal that something real happened. Keep both faces in frame if you recorded video.",
          },
        ],
      },
    ],
    faqs: [
      {
        question: "How many clips should I pull from one episode?",
        answer:
          "Most episodes hold three to ten clips worth posting, and long interviews can hold more. Pull only the moments that stand alone, because posting weak clips trains an audience to scroll past your name the next time it appears.",
      },
      {
        question: "What makes a podcast clip get watched?",
        answer:
          "The first five seconds decide it. Start inside the moment, keep one idea per clip, add captions for sound off viewing, and cut anything that explains what is about to happen instead of showing it.",
      },
      {
        question: "Should I clip live or after the episode?",
        answer:
          "After. Scanning the transcript for energy shifts, laughter and disagreements finds moments you will miss while recording, and it lets you pick the exact sentence the clip should open on.",
      },
      {
        question: "Do podcast clips need captions?",
        answer:
          "Yes. A large share of short form viewing happens with the sound off, and a conversation clip is nothing but words. Burned in captions also make the clip readable in a feed preview before anyone taps it.",
      },
    ],
    related: [
      "/features/auto-clip",
      "/use-cases/podcast-clipping",
      "/calculators/clips-from-one-video",
    ],
  },
  {
    slug: "streamer-clip-ideas",
    navTitle: "Streamer Clip Ideas",
    keyword: "Streamer Clip Ideas That Get Reposted",
    seoDescription:
      "Streamer clip ideas that get reposted, grouped by moment type, each with what to look for in the VOD and how to cut it for short form.",
    intro:
      "Use this list when you scan a stream VOD for clips worth posting. Each entry describes a moment type, what it looks like on the timeline, and how to cut it so it lands for someone who has never watched the channel. Reaction beats gameplay, so start on the face and the audio.",
    howToPick: [
      "Pick moments that read without knowing the game, the run or the running joke.",
      "Start on the reaction rather than the setup, and let the caption carry any context.",
      "Cut everything that is not building toward the payoff, including dead air between words.",
      "Check the clip policy of the channel before you post, and credit it clearly.",
    ],
    makeWith: {
      featurePath: "/features/auto-clip",
      featureLabel: "Auto Clip",
      createPath: "/create/auto-clip",
    },
    sections: [
      {
        id: "rage-and-tilt",
        title: "Rage and tilt",
        description: "Frustration reads to anyone, which is why it travels off platform.",
        ideas: [
          {
            title: "The Slow Motion Meltdown",
            note: "Frustration that builds over thirty seconds rather than exploding at once. Start where the tone first shifts, not at the loudest point.",
          },
          {
            title: "The Defeated Silence",
            note: "The quiet after a loss lands harder than shouting. Keep the silence intact and let the caption say what just happened.",
          },
          {
            title: "The Rage That Turns Into Laughing",
            note: "The turn from anger to laughter is the clip. Cut so both halves fit inside twenty seconds.",
          },
        ],
      },
      {
        id: "clutch-moments",
        title: "Clutch moments",
        description: "Skill clips only work when the stakes are stated up front.",
        ideas: [
          {
            title: "The Impossible Clutch",
            note: "Put the odds in the caption before the play so viewers know what they are watching. Without the stakes it is just movement on screen.",
          },
          {
            title: "The Reaction Before The Kill Confirms",
            note: "The face changes a beat before the outcome appears. Frame the camera larger than the game feed for this one.",
          },
          {
            title: "The Comeback From Zero",
            note: "Compress a long recovery into the two moments that mattered. Timestamps on screen help viewers feel the gap you cut out.",
          },
        ],
      },
      {
        id: "chat-interaction",
        title: "Chat interaction",
        description: "Clips where the audience is the second character.",
        ideas: [
          {
            title: "The Chat Prediction That Came True",
            note: "Show the chat message, then the moment it happens. Overlay the message so both land in the same frame.",
          },
          {
            title: "The Streamer Getting Roasted By Their Own Chat",
            note: "Self deprecating moments repost well because the streamer is clearly in on it. Keep the chat readable at phone size.",
          },
          {
            title: "The Donation Read That Goes Somewhere Unexpected",
            note: "A message that starts ordinary and turns. Cut on the moment the tone changes, not before it.",
          },
        ],
      },
      {
        id: "comic-timing",
        title: "Comic timing",
        description: "The comedy is usually in the recovery, not the joke.",
        ideas: [
          {
            title: "The Sigh After A Line That Did Not Land",
            note: "Hold two extra seconds after the sigh. Trimming to the joke removes the part that actually works.",
          },
          {
            title: "The Delayed Realization",
            note: "Someone understands what happened several seconds late. The gap is the joke, so do not tighten it.",
          },
          {
            title: "The Bit That Almost Does Not Land, Then Does",
            note: "Keep the uncomfortable stretch in the middle. Cutting it removes the reason the payoff works.",
          },
        ],
      },
      {
        id: "reactions-and-collabs",
        title: "Reactions and collabs",
        description: "Unguarded moments and two person chemistry.",
        ideas: [
          {
            title: "The Uncanny Character Comparison",
            note: "Chat decides the streamer looks like something and the streamer sees it too. Split screen the comparison for the repost.",
          },
          {
            title: "The Genuine Emotional Reaction",
            note: "Milestones, gifts and messages from viewers produce unguarded moments. Post these carefully and skip anything that feels intrusive.",
          },
          {
            title: "The Collab Chemistry Moment",
            note: "Two streamers finish the same sentence or fall apart at the same time. Include both cameras and both audio tracks.",
          },
        ],
      },
      {
        id: "fails",
        title: "Fails",
        description: "The shortest clips on this list and often the most reposted.",
        ideas: [
          {
            title: "The Technical Fail Mid Sentence",
            note: "A crash, a disconnect or a mic drop while something important is being said. The abrupt end is the punchline, so cut on it.",
          },
          {
            title: "The Confidently Wrong Prediction",
            note: "Clip the confident claim and the immediate contradiction back to back. Nothing else belongs in the clip.",
          },
          {
            title: "The Fall From A Height Reaction",
            note: "A fall with a real reaction is the cleanest repost material there is. Under ten seconds, no setup, no caption needed.",
          },
        ],
      },
    ],
    faqs: [
      {
        question: "What kind of stream clips travel off platform?",
        answer:
          "Unplanned reactions. Skilled play is impressive to people who already know the game, but a genuine reaction reads to anyone scrolling, which is why reaction clips get reposted far more often than highlight plays do.",
      },
      {
        question: "Do I need permission to clip another streamer?",
        answer:
          "Check the channel rules first. Many streamers publish a clip policy, some require credit and some ban reuploads entirely. When you do post, credit the channel by name in the caption and on screen, and take the clip down if they ask.",
      },
      {
        question: "How long should a stream clip be?",
        answer:
          "Under thirty seconds for most moments and under ten for a pure reaction. Cut every pause that is not part of the joke, and end on the payoff rather than on the aftermath.",
      },
      {
        question: "Is it better to clip live or from the VOD?",
        answer:
          "From the VOD. Live clipping catches the moment but rarely the right start point, while a VOD pass lets you scan for volume spikes and chat surges and choose the exact frame to open on.",
      },
    ],
    related: ["/features/auto-clip", "/use-cases/streamers", "/calculators/clips-from-one-video"],
  },
  {
    slug: "brainrot-video-ideas",
    legacy: true,
    navTitle: "Brainrot Video Ideas",
    keyword: "Brainrot Video Ideas for TikTok and Shorts",
    seoDescription:
      "Brainrot video ideas for TikTok and Shorts, grouped by format, each with the visual, the voice and the joke structure behind the concept.",
    intro:
      "Brainrot is a short form format built on absurd characters, deadpan delivery and AI generated visuals. These ideas give you the concept, the image style and the joke structure for each one. They are made to be produced fast and posted often, so treat volume as the plan and polish as optional.",
    howToPick: [
      "Pick a concept you can produce in under an hour, because posting frequency matters more than finish.",
      "Choose a joke structure that survives a flat delivery, since deadpan is the whole format.",
      "Favour ideas with a repeatable template so one that works turns into a series.",
    ],
    makeWith: {
      featurePath: "/features/ai-image",
      featureLabel: "AI Images",
      createPath: "/tools/images",
    },
    sections: [
      {
        id: "absurd-hybrid-characters",
        title: "Absurd hybrid characters",
        description: "The core of the format, and the easiest thing to turn into a series.",
        ideas: [
          {
            title: "The Object Animal Fusion Origin Story",
            note: "Generate one absurd hybrid and narrate how it came to exist in a serious documentary tone. The gap between the visual and the voice is the joke.",
          },
          {
            title: "The Hybrid Character Job Interview",
            note: "Put the creature through a normal interview with normal questions. Keep the questions boring so the answers do all the work.",
          },
          {
            title: "The Hybrid Character Family Tree",
            note: "Introduce a lineage of related creatures with names and traits. Every relative is a new post, which is why this one runs long.",
          },
          {
            title: "Rating Hybrid Characters By Power Level",
            note: "Assign numbers with total confidence and no justification. The arbitrary ranking is what people argue about in the comments.",
          },
        ],
      },
      {
        id: "quiz-and-trivia",
        title: "Quiz and trivia",
        description: "Fast formats that pull replies without needing a punchline.",
        ideas: [
          {
            title: "Name That Meme In Three Seconds",
            note: "Flash an image with a countdown and reveal the answer. Speed is the format, so do not add commentary.",
          },
          {
            title: "Which Character Are You Based On One Answer",
            note: "Ask one question, then assign a result with no logic connecting them. Keep the whole thing under fifteen seconds.",
          },
          {
            title: "Impossible Trivia, Deadpan Delivery",
            note: "Ask questions nobody can answer and give the answers flatly. The tone should never acknowledge how absurd it is.",
          },
        ],
      },
      {
        id: "pov-skits",
        title: "POV skits",
        description: "Static camera, escalating nonsense, one straight character.",
        ideas: [
          {
            title: "POV: You Are The Last Normal Person In The Room",
            note: "Everyone else behaves absurdly while the camera holds still. The stillness is what sells it.",
          },
          {
            title: "POV: Your Brain At 3am",
            note: "Stack unrelated intrusive thoughts on screen at increasing speed. End on the most mundane one in the pile.",
          },
          {
            title: "POV: The Group Chat Became Sentient",
            note: "Messages start answering questions nobody asked. Build to one line that is genuinely unsettling.",
          },
        ],
      },
      {
        id: "chaotic-narration",
        title: "Chaotic narration",
        description: "Voice driven formats where the mismatch is the entire idea.",
        ideas: [
          {
            title: "Overly Dramatic Narration Of A Mundane Event",
            note: "Score a trivial moment like a finale. Match the music cue to the least important beat in the clip.",
          },
          {
            title: "The Nature Documentary Voice, Wrong Subject",
            note: "Narrate people or objects as if they were wildlife. Commit fully to the vocabulary and never break it.",
          },
          {
            title: "The Conspiracy Theory About Something Harmless",
            note: "Build an elaborate case about something with no stakes at all. Arrows and circles on screen sell the investigation.",
          },
        ],
      },
      {
        id: "fake-interview",
        title: "Fake news and interviews",
        description: "Formats that borrow a familiar structure and put nothing inside it.",
        ideas: [
          {
            title: "Man On The Street, Wrong Planet",
            note: "Ask normal street interview questions in an absurd setting. The interviewer stays professional the whole way through.",
          },
          {
            title: "The Press Conference For A Fictional Crisis",
            note: "Stage a briefing about a problem that does not matter. Keep the format exactly like a real one, down to the podium.",
          },
          {
            title: "Breaking News, Nothing Happened",
            note: "Run a full news package where the story is that nothing occurred. The text along the bottom does most of the comedy.",
          },
        ],
      },
      {
        id: "nostalgia-mashup",
        title: "Nostalgia mashups",
        description:
          "Recognition does the work, so keep the reference generic rather than licensed.",
        ideas: [
          {
            title: "Childhood Cartoon Characters In A Corporate Meeting",
            note: "Put familiar archetypes into quarterly review language. Use generic archetypes rather than copyrighted characters.",
          },
          {
            title: "The Y2K Internet Character Comeback",
            note: "Bring an early internet aesthetic back as if it never left. Lean on the visual style, since that is what people recognize.",
          },
        ],
      },
    ],
    faqs: [
      {
        question: "What does brainrot mean as a video format?",
        answer:
          "It describes absurdist short form video built on surreal characters, nonsense logic and deadpan narration, usually made with AI images and AI voices. The word started as a joke about endless scrolling, and creators adopted it as a genre label.",
      },
      {
        question: "Are brainrot videos generated or filmed?",
        answer:
          "Most are generated. AI images supply the characters, an AI voice reads the narration and the edit stitches them together, which is why one person can post several a day without a camera, a set or anyone else involved.",
      },
      {
        question: "Can brainrot channels be monetized?",
        answer:
          "They use the same paths as any other short form channel, including platform payouts, sponsorship and traffic to something you sell. Advertiser rates on absurdist content are low, so most of these channels lean on volume and audience size rather than CPM.",
      },
      {
        question: "Do I need editing skills to make these?",
        answer:
          "Not many. The format tolerates rough cuts and simple captions, and consistency matters far more than finish. Spend your time on the concept and the voice delivery rather than on transitions nobody will notice.",
      },
    ],
    related: [
      "/features/ai-image",
      "/use-cases/brainrot-channels",
      "/features/ai-voiceover",
      "/calculators/posts-to-a-million-views",
    ],
  },
  {
    slug: "tiktok-hook-ideas",
    navTitle: "TikTok Hook Ideas",
    keyword: "TikTok Hook Ideas and Opening Lines That Stop the Scroll",
    seoDescription:
      "TikTok hook ideas and opening lines grouped by pattern, each with when to use it and what to cut to next so the first two seconds do the work.",
    intro:
      "Every line here is an opening you can say and put on screen inside the first two seconds. They are grouped by pattern, and each one tells you when it fits and what should follow it. Analyses of short form performance found identity, contrarian, open loop and confession hooks test strongest, so most of the list sits in those four.",
    howToPick: [
      "Keep the spoken hook under about twelve words so it lands inside the first two seconds.",
      "Pick a pattern that matches what actually happens in the video, because an unearned hook costs you the next post.",
      "Show the outcome while you say the line rather than promising it later.",
      "Put the line on screen as text as well, since many viewers start with the sound off.",
    ],
    makeWith: {
      featurePath: "/features/ai-voiceover",
      featureLabel: "AI Voiceover",
      createPath: "/tools/voiceovers",
    },
    sections: [
      {
        id: "curiosity-gap",
        title: "Curiosity gap",
        description: "Open a loop the viewer needs closed, then close it fast.",
        ideas: [
          {
            title: "Nobody talks about this part of it.",
            note: "Use when your angle is a detail the popular version of the topic leaves out. Cut straight to that detail and never to an introduction.",
          },
          {
            title: "I did not believe this until I tried it myself.",
            note: "For results you can show. Cut to the proof shot within two seconds or the line reads as an unsupported claim.",
          },
          {
            title: "This only works if you do it in the wrong order.",
            note: "Use on process content where the sequence is the insight. Cut to someone doing it the usual way and failing.",
          },
          {
            title: "There is one detail everyone skips in this story.",
            note: "For retellings of something the audience already knows. Cut to the skipped detail, then rebuild the story around it.",
          },
        ],
      },
      {
        id: "contrarian",
        title: "Contrarian strike",
        description: "Contradict the common advice, then immediately justify it.",
        ideas: [
          {
            title: "Everyone tells you to do this. Do not.",
            note: "Use when you can back the reversal with a specific reason. Cut to the reason, not to your credentials.",
          },
          {
            title: "The advice you were given is outdated by about five years.",
            note: "Best when something concrete changed. Cut to what changed and name it in the same breath.",
          },
          {
            title: "I used to agree with this. Then this happened.",
            note: "Softer than a flat contradiction, so it works on audiences who hold the belief. Cut to the event that changed your mind.",
          },
          {
            title: "This popular tip actually makes the problem worse.",
            note: "Use when you can show the failure state. Cut to the failure on screen before you explain the mechanism.",
          },
        ],
      },
      {
        id: "confession",
        title: "Confession",
        description: "Signal that something is being said for the first time, then say it.",
        ideas: [
          {
            title: "I have never told anyone this part.",
            note: "For personal stories with a real omission. Cut to the omitted part quickly, since delay reads as bait.",
          },
          {
            title: "This is the first time I am saying this out loud.",
            note: "Use when the video is the disclosure itself. Cut to the disclosure and not to background.",
          },
          {
            title: "I was wrong about this for three years.",
            note: "Pairs a confession with a number, which makes it concrete. Cut to what you were wrong about.",
          },
          {
            title: "Something happened last week that I am still processing.",
            note: "Signals a story that has not resolved. Cut into the middle of the event with the camera already running.",
          },
        ],
      },
      {
        id: "identity-call",
        title: "Identity call",
        description: "Name a specific kind of person so they recognize themselves instantly.",
        ideas: [
          {
            title: "If you grew up checking the locks twice, this is for you.",
            note: "Use when the whole video speaks to one type of person. Cut to the behavior you are describing so they see themselves in it.",
          },
          {
            title: "This is for anyone who has always been the responsible one.",
            note: "Broad enough to catch a lot of people and specific enough to feel personal. Cut to the situation rather than to a definition.",
          },
          {
            title: "If you rewrite a text five times before sending it, watch this.",
            note: "The most specific version of the pattern, which is why it works. Cut to the phone screen.",
          },
          {
            title: "This is for the person who always says they are fine.",
            note: "For emotional content where the payoff is being understood. Cut to a quiet visual and lower your delivery.",
          },
        ],
      },
      {
        id: "numbers-and-specifics",
        title: "Numbers and specifics",
        description: "A precise figure sounds like a report rather than a pitch.",
        ideas: [
          {
            title: "It took exactly eleven minutes for this to fall apart.",
            note: "Use when you can show the aftermath. Cut to the wreckage and work backward from there.",
          },
          {
            title: "Three messages in, I knew something was wrong.",
            note: "For text and chat stories. Cut to the third message on screen and let people read it.",
          },
          {
            title: "I tested this for thirty days and only one thing changed.",
            note: "Sets up a single payoff instead of a list. Cut to the one thing before you explain the test.",
          },
          {
            title: "There is one number in this story that changes everything.",
            note: "Use when the figure is genuinely surprising. Put it on screen and say it out loud at the same moment.",
          },
        ],
      },
      {
        id: "mid-action-cold-open",
        title: "Mid action cold open",
        description: "Start inside the scene and supply context afterward.",
        ideas: [
          {
            title: "So he just said that. In front of everyone.",
            note: "Drops the viewer into a scene already in progress. Cut to your reaction, then fill in the context after.",
          },
          {
            title: "Wait, go back. Read that message again.",
            note: "For screen recordings and chat threads. Cut to the message with the relevant line highlighted.",
          },
          {
            title: "This is the exact moment it went wrong.",
            note: "Use over footage where the mistake is visible. Cut to the frame and freeze it.",
          },
          {
            title: "Stop. Before you hear the rest, guess what happens next.",
            note: "Invites a comment before the payoff arrives. Cut to a two second gap, then the answer.",
          },
        ],
      },
      {
        id: "warning",
        title: "Warning",
        description: "Position the video as something the viewer needs before they act.",
        ideas: [
          {
            title: "If you are about to do this, watch the next ten seconds.",
            note: "Use when the video prevents a specific mistake. Cut to the mistake happening rather than to advice.",
          },
          {
            title: "Check this before you sign anything.",
            note: "Works for contracts, rentals and subscriptions. Cut to the clause or the line item on screen.",
          },
          {
            title: "Do not send that message yet.",
            note: "For interpersonal advice. Cut to a draft on screen and read the problem line out loud.",
          },
          {
            title: "You have probably already made this mistake today.",
            note: "A broad accusation that most viewers will test against themselves. Cut to the mistake immediately so it does not feel like a tease.",
          },
        ],
      },
      {
        id: "direct-question",
        title: "Direct question",
        description: "Ask something the viewer answers in their head before they can scroll.",
        ideas: [
          {
            title: "How much do you think this actually cost?",
            note: "Use when the real number is far from the guess. Cut to the item, hold two seconds, then reveal.",
          },
          {
            title: "Why does nobody explain this properly?",
            note: "For explainers on something widely taught badly. Cut to your explanation with no preamble.",
          },
          {
            title: "Can you spot what is wrong with this?",
            note: "Use over an image or clip with a visible flaw. Cut to a tight shot and let people look before you answer.",
          },
          {
            title: "What would you have done here?",
            note: "For story content with a genuine dilemma. Cut into the dilemma itself and save your own answer for the end.",
          },
        ],
      },
    ],
    faqs: [
      {
        question: "How long should a TikTok hook be?",
        answer:
          "Under about twelve spoken words, delivered in the first one to two seconds. Anything longer and the decision to scroll has already been made, so the rest of the sentence plays to people who are gone.",
      },
      {
        question: "Is curiosity or shock the better hook?",
        answer:
          "Curiosity, contrarian, confession and identity call openings test strongest in published analyses of short form performance. Pure shock earns the stop but not the watch, because there is nothing left to find out once the shocking thing has been shown.",
      },
      {
        question: "Should the hook be spoken or on screen?",
        answer:
          "Both, at the same time. A large share of viewers start with the sound off and the text is what stops them, while the spoken line carries the tone. Keep the on screen version short enough to read at a glance.",
      },
      {
        question: "Why do polished intros perform worse?",
        answer:
          "Branded openings, logo stings and greetings read as advertising, and people scroll past advertising by reflex. Analyses found that showing the outcome in the first two seconds roughly doubles views compared with the weakest hook types, so start at the result.",
      },
    ],
    related: [
      "/features/ai-voiceover",
      "/features/auto-clip",
      "/use-cases/clippers",
      "/calculators/posts-to-a-million-views",
    ],
  },
];

export const IDEA_NICHE_MAP: Record<string, IdeaNiche> = Object.fromEntries(
  IDEA_NICHES.map((niche) => [niche.slug, niche]),
);

export const ideaCount = (niche: IdeaNiche) =>
  niche.sections.reduce((total, section) => total + section.ideas.length, 0);

export const ideaTitle = (niche: IdeaNiche) => `${ideaCount(niche)} ${niche.keyword}`;

export const ideaPath = (slug: string) => `/ideas/${slug}`;
